<script setup lang="ts">
import {
  PaginatedQuery,
  ClientInvite,
  ClientInviteListFilters,
  PaginatedResponse,
  ProductStatus,
  showProducts,
} from 'ah-api-gateways';
import { computed, PropType, useAttrs, useListeners } from 'vue';
import { defineUseManagedListingProps, useManagedListing, UseManagedListingEmits } from 'ah-common-lib/src/listing';
import { useServices } from 'bd-common/src/services';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import MaterialIcon from 'bd-common/src/components/common/MaterialIcon.vue';
import { mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { capitalize } from 'lodash';

const props = defineProps({
  config: {
    type: Object as PropType<{ tableFields: any[] }>,
    default: () => ({ tableFields: [] }),
  },
  ...defineUseManagedListingProps<ClientInvite>(),
});

const services = useServices();
const attrs = useAttrs();
const listeners = useListeners();

interface InivitedClientEmit extends UseManagedListingEmits<ClientInvite> {}

const emit = defineEmits<InivitedClientEmit>();

const managedListing = useManagedListing<ClientInvite>({
  loadDataRequest(query: PaginatedQuery<ClientInviteListFilters>) {
    // FIXME - we need to update this with the products endpoint
    return services.clientInvites.listClientInvites(query).pipe(
      mergeMap(() => {
        const empty = {
          list: [
            {
              sellCurrency: 'EUR',
              buyCurrency: 'GBP',
              currencyPair: 'EURGBP',
              status: 'ENABLED',
              notionalAmount: 12,
              notionalCurrency: 'GBP',
              products: ['SPOT', 'FORWARD'],
            },
            {
              sellCurrency: 'AED',
              buyCurrency: 'USD',
              currencyPair: 'AEDUSD',
              status: 'DISABLED',
              notionalAmount: 10000,
              notionalCurrency: 'AED',
              products: ['SPOT'],
            },
            {
              sellCurrency: 'AED',
              buyCurrency: 'USD',
              currencyPair: 'AEDUSD',
              status: 'DISABLED',
              notionalAmount: 200,
              notionalCurrency: 'AED',
              products: ['FORWARD'],
              updatedBy: 'Annie Appleseed',
              updatedAt: '2024-03-25T15:57:23.559Z',
              previousStatus: 'ENABLED',
              previousProducts: ['SPOT'],
            },
          ],
          pageNumber: 0,
          pageSize: 10,
          total: 3,
        } as PaginatedResponse<any>;
        return of(empty);
      })
    );
  },
  emit,
  props,
  fields: computed(() => props.config.tableFields),
  reqManager: useRequestManager().manager,
});

function iconName(status: ProductStatus) {
  return status === ProductStatus.ENABLED ? 'check_circle' : 'motion_photos_off';
}

function iconClass(status: ProductStatus) {
  return status === ProductStatus.ENABLED ? 'icon-enabled' : 'icon-disabled';
}

defineExpose({ loadData: managedListing.loadData });
</script>

<template>
  <PaginatedTable
    with-simple-scroll
    class="invited-clients-table"
    hover
    rowClass="invited-client-row"
    items-label="invited clients"
    v-bind="{ ...managedListing.bindings, ...attrs }"
    v-on="{ ...managedListing.listeners, ...listeners }"
  >
    <template #cell(status)="{ item }">
      <VRow alignV="center">
        <VCol cols="1" align-self="center"
          ><MaterialIcon class="status-icon" :class="iconClass(item.status)" :icon="iconName(item.status)"
        /></VCol>
        <VCol align-self="center" class="status-banner">{{ capitalize(item.status) }}</VCol>
      </VRow>
    </template>
    <template #cell(notionalValue)="{ item }">
      {{ `${item.notionalCurrency} ${formatCurrencyValue(item.notionalAmount)}` }}
    </template>
    <template #cell(products)="{ item }">
      {{ showProducts(item.products) }}
    </template>
    <template #cell(actions)="{ item }">
      <MaterialIcon
        class="go-to-history"
        @click="$emit('view-history', item)"
        icon="visibility"
        v-if="item.updatedBy"
      />
    </template>
    <template v-for="(_, name) in $scopedSlots" v-slot:[name]="scope">
      <slot :name="name" v-bind="scope" />
    </template>
  </PaginatedTable>
</template>

<style lang="scss" scoped>
::v-deep {
  .actions-cell {
    text-align: right;
    width: 2em;

    .go-to-history {
      font-size: 2em;
      color: getColor($color-bdPrimary);
      cursor: pointer;
    }
  }

  .status-banner {
    line-height: 0.7em;
  }

  .status-icon {
    font-size: 1.7em;
    font-weight: 500;
    line-height: 0.7em;

    &.icon-enabled {
      color: getColor($color-bdSuccess);
    }
    &.icon-disabled {
      color: getColor($color-bdMediumGrey);
    }
  }
}
</style>
